import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import { Button, TextInput, Select } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { ActivityBlockType } from '@/models/Activity';

type ActivityBlockInlineFormFields = {
  name: string;
  activityType: ActivityBlockType;
};

type ActivityBlockInlineFormProps = {
  defaultValues?: ActivityBlockInlineFormFields;
  onSubmit: (data: ActivityBlockInlineFormFields) => void;
  onCancel: () => void;
  isLoading?: boolean;
  showBlockTypeSelector?: boolean;
};

export default function ActivityBlockInlineForm({
  defaultValues,
  onSubmit,
  onCancel,
  isLoading,
  showBlockTypeSelector = true,
}: ActivityBlockInlineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab',
  });
  const { t: tActivityBlockTypes } = useTranslation('translation', {
    keyPrefix: 'activityBlockTypes',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ActivityBlockInlineFormFields>({
    defaultValues: defaultValues ?? {
      name: '',
      activityType: ActivityBlockType.REGULAR,
    },
  });

  return (
    <FieldsetContainer>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <TextInput.Fieldset
          label={t('blockName')}
          labelAlt={errors?.name?.message}
        >
          <TextInput
            type="text"
            placeholder={t('blockNamePlaceholder')}
            className="w-full"
            color={!errors?.name?.message ? 'default' : 'error'}
            {...register('name', { required: t('blockNameRequired') })}
          />
        </TextInput.Fieldset>
        {showBlockTypeSelector && (
          <Fieldset label={t('blockType')}>
            <Select className="w-full" {...register('activityType')}>
              <Select.Option value={ActivityBlockType.REGULAR}>
                {tActivityBlockTypes(ActivityBlockType.REGULAR)}
              </Select.Option>
              <Select.Option value={ActivityBlockType.MANUAL_GRADE}>
                {tActivityBlockTypes(ActivityBlockType.MANUAL_GRADE)}
              </Select.Option>
            </Select>
          </Fieldset>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="grow bg-base-300"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            className="grow"
            disabled={isLoading}
            loading={isLoading}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FieldsetContainer>
  );
}
